import React, { useState } from 'react';
import FenetreRetour from '../components/FenetreRetour';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HelpCircle, BriefcaseBusiness, UserCog, GraduationCap,
  Package, FolderOpen, ChevronDown, ChevronRight, CheckCircle2,
  ArrowRight, Lightbulb, AlertCircle, ShieldCheck, FileText, MessageSquareText,
  MessageSquarePlus, TriangleAlert,
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'acces-acteurs',
    icon: ShieldCheck,
    color: 'text-[var(--vert-fonce)] bg-[var(--vert-pale)] border-[var(--border)]',
    title: 'Accès des acteurs à leur espace',
    subtitle: 'Comment chaque acteur se connecte à la plateforme',
    steps: [
      {
        num: '1',
        title: 'Association (dirigeant ou membre)',
        color: 'bg-[var(--nuit)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>L'association accède à son espace via le <strong>front-end utilisateur</strong> (URL distincte de l'admin).</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Elle crée un compte ou reçoit ses identifiants depuis la plateforme de diagnostic existante</li>
              <li>À la connexion, elle voit la liste de ses <strong>projets d'accompagnement actifs</strong></li>
              <li>Depuis le tableau de bord projet, elle accède à : Diagnostic · Actions · Documents · Formation · Kit</li>
            </ul>
            <p className="text-xs text-[var(--text-muted)] mt-2">Rôles concernés : <span className="font-semibold text-[var(--text-bold)]">DIRIGEANT</span> et <span className="font-semibold text-[var(--text-bold)]">MEMBRE</span>.</p>
          </div>
        ),
      },
      {
        num: '2',
        title: 'Coach, mentor, expert',
        color: 'bg-[var(--nuit)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>Le coach, le mentor, le formateur et l'expert accèdent tous à l'<strong>espace admin</strong>
               (même URL que l'administrateur) et partagent le même rôle technique. Ce qui les distingue est
               leur <strong>profil</strong>, visible sur leur fiche.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Son compte est créé par un admin depuis la page <strong>Coaches et mentors</strong>,
                  ou automatiquement à l'acceptation de sa candidature</li>
              <li>Il reçoit par e-mail un <strong>lien de première connexion</strong>, valable une seule fois</li>
              <li>En le suivant, il complète son profil et choisit lui-même son mot de passe — personne d'autre ne le connaît</li>
              <li>Il ne voit que les projets qui lui sont <strong>affectés</strong> — aucune autre donnée n'est visible</li>
            </ul>
            <p className="text-xs text-[var(--text-muted)] mt-2">Rôle : <span className="font-semibold text-[var(--text-bold)]">COACH</span>.</p>
          </div>
        ),
      },
      {
        num: '3',
        title: 'Administrateur',
        color: 'bg-[var(--text-muted)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>Le compte est ouvert par un super administrateur, depuis
               <strong> Rôles et accès</strong>. Comme pour les autres, aucun mot de
               passe n'est créé : la personne reçoit un <strong>lien de première
               connexion</strong> et choisit le sien.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Ce qu'un rôle ouvre dépend de la <strong>zone</strong>, pas de la page : la liste est la même pour le menu et pour le serveur</li>
              <li>Le <span className="font-semibold">super administrateur</span> est seul à créer un compte, changer un rôle ou supprimer</li>
              <li>La session se ferme après <strong>30 minutes sans activité</strong></li>
              <li>Mot de passe perdu ou compte suspect : <strong>« Renvoyer un lien de connexion »</strong>. L'ancien mot de passe cesse aussitôt de fonctionner</li>
            </ul>
            <p className="text-xs text-[var(--text-muted)] mt-2">Rôles : <span className="font-semibold text-[var(--text-main)]">ADMIN</span> · <span className="font-semibold text-[var(--text-main)]">SUPER_ADMIN</span> · <span className="font-semibold text-[var(--text-main)]">ANALYSTE</span>.</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">Ce que chaque rôle autorise se règle zone par zone dans
               <strong> Rôles et accès</strong> → onglet « Rôles et permissions ». Quatre cases par
               rôle et par zone : <strong>V</strong>oir, <strong>A</strong>jouter,
               <strong> M</strong>odifier, <strong>E</strong>xporter. Fermer « exporter » laisse
               la page lisible mais retire le téléchargement du rapport ; fermer « voir » retire
               la page du menu <em>et</em> répond « page introuvable » à qui en saisit l'adresse.
               Le tableau n'est pas une description : c'est la règle que le serveur applique, et
               elle prend effet dès l'enregistrement. Seul un super administrateur peut la modifier,
               et il ne peut pas se fermer « Rôles et accès » à lui-même.</p>
          </div>
        ),
      },
      {
        num: '4',
        title: 'Comment créer les comptes ?',
        color: 'bg-[#5EBD4F]',
        content: (
          <div className="space-y-3 text-sm text-[var(--text-main)]">
            <div className="bg-[var(--bg-main)] rounded-[14px] p-3">
              <p className="font-semibold text-[var(--text-main)] mb-1">Association — accès automatique</p>
              <p>Lors de la <strong>création d'un projet</strong>, un accès est ouvert pour l'organisation à l'adresse e-mail de sa fiche. <strong>Vous n'avez rien à noter ni à transmettre</strong> : un lien d'activation part directement vers cette adresse.</p>
              <p className="mt-2">Si l'organisation n'a pas d'adresse, ou si celle-ci appartient déjà à un autre compte, la fenêtre vous le dit et aucun accès n'est ouvert. Corrigez l'adresse dans la fiche de l'organisation, puis utilisez <strong>« Renvoyer le lien »</strong> sur le projet.</p>
            </div>
            <div className="encart encart-info">
              <p className="font-semibold text-[var(--text-bold)] mb-1">Première connexion de l'association</p>
              <p>L'association ouvre le lien reçu par e-mail et choisit son mot de passe. <strong>Son identifiant est son adresse e-mail.</strong> Le lien ne sert qu'une fois et personne, pas même l'équipe ASSO-PRO, ne connaît le mot de passe choisi.</p>
              <p className="mt-2">Mot de passe oublié : l'association demande un code à six chiffres depuis la page de connexion, et le reçoit sur la même adresse.</p>
            </div>
            <div className="bg-[var(--bg-main)] rounded-[14px] p-3">
              <p className="font-semibold text-[var(--text-main)] mb-1">Coach, mentor, expert</p>
              <p>Page <strong>Coaches et mentors</strong> → « Nouvel intervenant » → choisir le profil, puis renseigner
                 identifiant et adresse e-mail. Le lien de première connexion part automatiquement.</p>
            </div>
            <div className="bg-[var(--bg-main)] rounded-[14px] p-3">
              <p className="font-semibold text-[var(--text-main)] mb-1">Nouvel Admin</p>
              <p>Page <strong>Gestion Admins</strong> (super administrateur uniquement) → « Nouvel accès » → identifiant, adresse e-mail et rôle. Aucun mot de passe n'est créé : la personne choisit le sien depuis le lien reçu.</p>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'workflow',
    icon: BriefcaseBusiness,
    color: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]',
    title: 'Parcours complet d\'un projet',
    subtitle: 'De la création jusqu\'à la fin de l\'accompagnement',
    steps: [
      {
        num: '1',
        title: 'Créer le projet',
        color: 'bg-[var(--nuit)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>Allez dans <strong>Projets</strong> → cliquez <strong>"Créer un projet"</strong>.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Sélectionnez l'<strong>organisation</strong> (l'association à accompagner)</li>
              <li>Affectez un <strong>coach</strong> (optionnel, peut être fait plus tard)</li>
              <li>Cliquez <strong>"Créer le projet"</strong></li>
            </ul>
            <p className="text-xs text-[var(--text-muted)] mt-2">Le projet <strong>démarre aussitôt</strong> : statut <span className="font-semibold text-[var(--text-bold)]">En cours</span>, diagnostic initial créé, association prévenue. C'est vous qui l'ouvrez, la décision est déjà prise.</p>
          </div>
        ),
      },
      {
        num: '2',
        title: 'Valider une demande',
        color: 'bg-[var(--accent)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>La validation ne concerne que les projets <strong>demandés par une association</strong>, ou issus d'une demande reçue.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Sur la ligne du projet en attente, cliquez le bouton <strong>✓ (vert)</strong></li>
              <li>Confirmez ou changez le coach affecté</li>
              <li>Cliquez <strong>"Valider &amp; Démarrer"</strong></li>
            </ul>
            <p className="text-xs text-[var(--text-muted)] mt-2">Un projet que vous avez créé vous-même est déjà en cours : il n'y a rien à valider.</p>
          </div>
        ),
      },
      {
        num: '3',
        title: 'Créer le diagnostic initial',
        color: 'bg-[var(--nuit)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>Ouvrez le projet (<strong>icône œil</strong>) → onglet <strong>Diagnostics</strong> → <strong>"Nouveau diagnostic"</strong>.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Type : <strong>Initial</strong></li>
              <li>Le questionnaire référentiel est utilisé automatiquement</li>
            </ul>
            <p className="text-xs text-[var(--text-muted)] mt-2">L'association reçoit une notification et peut remplir le questionnaire depuis son espace.</p>
          </div>
        ),
      },
      {
        num: '4',
        title: 'Analyse et plan d\'actions',
        color: 'bg-[var(--attention)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>Une fois le questionnaire rempli par l'association, le coach :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Remplit sa propre évaluation (score expert)</li>
              <li>Les scores sont calculés automatiquement</li>
              <li>Génère le <strong>plan d'actions correctives</strong> depuis l'onglet Actions</li>
            </ul>
            <p className="text-xs text-[var(--text-muted)] mt-2">L'association peut voir et mettre à jour les statuts de ses actions.</p>
          </div>
        ),
      },
      {
        num: '5',
        title: 'Suivi — formation & documents',
        color: 'bg-[var(--accent)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>Durant les 6 mois d'accompagnement :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>L'association suit les <strong>modules de formation</strong> (onglet Formation)</li>
              <li>Elle dépose et soumet des <strong>documents</strong> du Kit ASSO PRO</li>
              <li>Le coach <strong>valide ou rejette</strong> les documents soumis avec un commentaire</li>
              <li>Les actions sont mises à jour par l'association</li>
            </ul>
          </div>
        ),
      },
      {
        num: '6',
        title: 'Diagnostic mi-parcours (3 mois)',
        color: 'bg-[var(--nuit)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>Même procédure que le diagnostic initial :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Type : <strong>Mi-parcours</strong></li>
              <li>Les scores sont comparés automatiquement avec l'initial</li>
              <li>Un nouveau plan d'actions est généré</li>
            </ul>
          </div>
        ),
      },
      {
        num: '7',
        title: 'Diagnostic final (6 mois)',
        color: 'bg-[var(--accent)]',
        content: (
          <div className="space-y-2 text-sm text-[var(--text-main)]">
            <p>Type : <strong>Final</strong> — même procédure.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Comparaison des 3 diagnostics (initial / mi-parcours / final)</li>
              <li>Rapport de progression complet visible dans le projet</li>
            </ul>
            <p className="text-xs text-[var(--text-muted)] mt-2">Clôturez le projet : statut → <span className="font-semibold text-[var(--accent-texte)]">Terminé</span>.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'coaches',
    icon: UserCog,
    color: 'text-[var(--text-bold)] bg-[var(--info-fond)] border-[var(--border)]',
    title: 'Gestion des coaches',
    subtitle: 'Créer et gérer les experts qui accompagnent les associations',
    items: [
      { q: 'Comment ajouter un coach ou un mentor ?', a: "Allez dans « Coaches et mentors » → « Nouvel intervenant ». Choisissez le profil (coach, mentor, formateur, expert, bénévole), puis renseignez le nom et l'adresse e-mail. Un lien de première connexion lui est envoyé : il choisit son mot de passe lui-même." },
      { q: 'Comment attribuer un coach ou un mentor à un projet ?', a: "À l'acceptation de la demande (« Demandes de service ») ou à la création du projet. Ensuite, à tout moment : page du projet → en haut, à côté de « Coach / mentor », « Attribuer » ou « Changer ». La liste propose coaches et mentors ; la personne choisie est prévenue et l'ancienne perd l'accès au projet." },
      { q: 'Que peut faire un coach ?', a: 'Le coach voit uniquement les projets qui lui sont affectés. Il peut remplir les évaluations expert, créer des actions, valider des documents soumis.' },
    ],
  },
  {
    id: 'formation',
    icon: GraduationCap,
    color: 'text-[var(--vert-fonce)] bg-[var(--vert-pale)] border-[var(--border)]',
    title: 'Gestion de la formation',
    subtitle: 'Créer les modules et ressources pédagogiques',
    items: [
      { q: 'Comment créer un module ?', a: 'Formation → "Nouveau module". Renseignez le titre, la catégorie (Gouvernance, Finances…), la description et l\'ordre d\'affichage.' },
      { q: 'Comment ajouter des ressources à un module ?', a: 'Cliquez sur un module → "Ajouter une ressource". Types disponibles : vidéo (URL), fichier PDF, quiz. Chaque ressource a un titre et un ordre.' },
      { q: 'Qu\'est-ce qu\'un quiz ?', a: 'Une ressource de type "quiz" permet à l\'association de tester ses connaissances. Ajoutez des questions avec des réponses multiples et marquez la bonne réponse.' },
      { q: 'Comment suivre la progression ?', a: 'Dans le détail d\'un projet → onglet Formation. Le taux de complétion par module est visible. L\'association met à jour sa progression elle-même.' },
    ],
  },
  {
    id: 'kit',
    icon: Package,
    color: 'text-[var(--attention)] bg-[var(--attention-fond)] border-[var(--border)]',
    title: 'Kit ASSO PRO',
    subtitle: 'Gérer les outils pratiques mis à disposition des associations',
    items: [
      { q: 'Comment ajouter un outil au kit ?', a: 'Kit ASSO PRO → "Ajouter une ressource". Renseignez le titre, la catégorie, la description. Uploadez le fichier modèle.' },
      { q: 'Qu\'est-ce que "modifiable" ?', a: 'Si "Modifiable" est coché, l\'association peut télécharger le modèle, le remplir, et le soumettre au coach pour validation.' },
      { q: 'Comment valider un document soumis ?', a: 'Dans le détail du projet → onglet Kit. Les soumissions apparaissent avec un bouton "Valider" ou "Rejeter". Vous pouvez ajouter un commentaire visible par l\'association.' },
    ],
  },
  {
    id: 'documents',
    icon: FolderOpen,
    color: 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]',
    title: 'Documents déposés',
    subtitle: 'Gérer les documents administratifs de l\'association',
    items: [
      { q: 'Comment voir les documents d\'un projet ?', a: 'Ouvrez le projet → onglet Documents. Tous les fichiers déposés par l\'association sont listés par catégorie.' },
      { q: 'Comment valider un document ?', a: 'Cliquez le bouton ✓ sur le document. Vous pouvez ajouter un commentaire. Le statut passe à "Validé" et l\'association en est notifiée.' },
      { q: 'Quelles sont les catégories de documents ?', a: 'Création, Gouvernance, Finances, Ressources humaines, Suivi-évaluation, Communication, Autre.' },
    ],
  },
  {
    id: 'rapport',
    icon: FileText,
    color: 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]',
    title: 'Rapport de diagnostic',
    subtitle: 'Consulter les résultats et produire le PDF',
    items: [
      { q: "Où voir les résultats d’un diagnostic ?",
        a: "Page Organisations, puis cliquez la structure. La fiche donne le profil moyen par dimension, la restitution, le plan d’action correctif et les scores par domaine." },
      { q: "Comment obtenir le PDF ?",
        a: "Bouton « Télécharger le PDF », en haut de la fiche. « Aperçu Rapport » montre d’abord à l’écran ce que contiendra le fichier : c’est le même document, pas une approximation." },
      { q: "Pourquoi le PDF met-il du temps ?",
        a: "La mise en page est faite par un navigateur, côté serveur, pour que le fichier soit identique à l’aperçu. Comptez une quinzaine de secondes ; le bouton affiche « Génération du PDF… » pendant ce temps. Ne relancez pas, la demande est en cours." },
      { q: "Le plan d’action est long à parcourir",
        a: "Le filtre en haut de la section le restreint à « Priorité urgente » ou « Priorité standard »." },
    ],
  },
  {
    id: 'messagerie',
    icon: MessageSquareText,
    color: 'text-[var(--text-main)] bg-[var(--bg-main)] border-[var(--border)]',
    title: 'Messagerie',
    subtitle: 'Répondre aux demandes de contact reçues du site',
    items: [
      { q: "D’où viennent ces messages ?",
        a: "Du formulaire de contact du site public. Chaque message est rattaché à l’organisation lorsqu’elle existe déjà dans la base." },
      { q: "Comment répondre ?",
        a: "Choisissez la conversation à gauche, écrivez en bas à droite. Entrée envoie, Maj+Entrée va à la ligne. La réponse part par e-mail à l’adresse de la demande et reste dans le fil." },
      { q: "Que veulent dire les deux coches ?",
        a: "Qu’une réponse est partie par e-mail. Elles ne disent pas que le destinataire l’a lue : la plateforme ne peut pas le savoir, et ne le prétend donc pas." },
      { q: "Puis-je joindre un document ?",
        a: "Oui, par le trombone : PDF, Word ou Excel. Le serveur vérifie le contenu du fichier, pas seulement son extension." },
    ],
  },
];

function StepCard({ step, index }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 rounded-controle ${step.color} text-white flex items-center justify-center text-xs font-semibold shrink-0`}>
          {step.num}
        </div>
        {index < 6 && <div className="w-0.5 bg-[var(--bg-main)] flex-1 mt-2 mb-0"  />}
      </div>
      <div className="pb-6 flex-1">
        <h4 className="font-semibold text-[#0A1128] mb-2">{step.title}</h4>
        {step.content}
      </div>
    </div>
  );
}

/* La premiere question de chaque section s'ouvre d'elle-meme.
   Une page d'aide entierement repliee ne montre que des titres : on ne
   sait pas a quoi ressemble une reponse, ni si elle vaut le clic. Une
   seule ouverte suffit a le dire, sans deplier tout le reste. */
function FaqItem({ q, a, ouvertParDefaut = false }) {
  const [open, setOpen] = useState(ouvertParDefaut);
  return (
    <div className="border border-[var(--border)] rounded-pastille overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[var(--bg-main)] transition-all"
      >
        <span className="font-semibold text-sm text-[var(--text-bold)]">{q}</span>
        {open ? <ChevronDown className="w-4 h-4 text-[var(--accent-texte)] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[var(--text-muted)] shrink-0" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-5 pb-4 text-sm text-[var(--text-main)] border-t border-[var(--border)] pt-3">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Section({ section }) {
  const [open, setOpen] = useState(section.id === 'workflow');
  const Icon = section.icon;

  return (
    <div className="bg-[var(--bg-card)] rounded-carte shadow-carte overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-6 hover:bg-[var(--bg-main)] transition-all text-left"
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-pastille border flex items-center justify-center ${section.color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="titre-carte text-[var(--text-bold)]">{section.title}</h2>
            <p className="text-xs text-[var(--text-muted)]">{section.subtitle}</p>
          </div>
        </div>
        {open
          ? <ChevronDown className="w-5 h-5 text-[var(--accent-texte)] shrink-0" />
          : <ChevronRight className="w-5 h-5 text-[var(--text-muted)] shrink-0" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="px-6 pb-6 border-t border-[var(--border)]">
              {section.steps ? (
                <div className="pt-6">
                  {section.steps.map((step, i) => (
                    <StepCard key={step.num} step={step} index={i} />
                  ))}
                </div>
              ) : (
                <div className="pt-4 space-y-3">
                  {section.items.map((item, i) => (
                    <FaqItem key={i} q={item.q} a={item.a} ouvertParDefaut={i === 0} />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Help() {
  // « probleme » ou « suggestion » : la fenêtre de retour ouverte, sinon null.
  const [retour, setRetour] = useState(null);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 rounded-pastille bg-[var(--accent)]/15 flex items-center justify-center">
          <HelpCircle className="w-6 h-6 text-[var(--accent-texte)]" />
        </div>
        <div>
          <h1 className="titre-page">Guide d'utilisation</h1>
          <p className="text-sm text-[var(--text-muted)]">Tout ce qu'il faut savoir pour gérer la plateforme ASSO PRO</p>
        </div>
      </div>

      {/* Résumé logique */}
      <div className="bg-[var(--accent)]/10 border border-[var(--accent)]/30 rounded-carte p-5 mb-8 flex gap-4">
        <Lightbulb className="w-5 h-5 text-[var(--accent-texte)] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-[var(--text-bold)] mb-1">La logique du module d'accompagnement</p>
          <div className="flex items-center gap-2 flex-wrap text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest">
            {['Créer projet', 'Diagnostic initial', 'Plan d\'actions', 'Formation & Documents', 'Mi-parcours', 'Diagnostic final', 'Clôture'].map((s, i, arr) => (
              <React.Fragment key={s}>
                <span className="text-[var(--accent-texte)]">{s}</span>
                {i < arr.length - 1 && <ArrowRight className="w-3 h-3 text-[var(--text-muted)]" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Avertissement */}
      <div className="encart encart-attention mb-8">
        <AlertCircle className="w-5 h-5 text-[var(--attention)] shrink-0 mt-0.5" />
        <p className="text-sm text-[var(--attention)]">
          <strong>Avant de commencer</strong> : assurez-vous d'avoir créé au moins un <strong>coach</strong> (page Coaches) et configuré des <strong>modules de formation</strong> (page Formation) et des <strong>ressources Kit</strong> (page Kit ASSO PRO).
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {SECTIONS.map(section => (
          <Section key={section.id} section={section} />
        ))}
      </div>

      {/* ── Nous écrire ───────────────────────────────────────────────────
          Placé APRÈS le guide : on cherche d'abord une réponse. Les
          formulaires ne vivent plus dans cette page : ils s'ouvrent au
          premier plan, comme depuis le menu du compte, sans quitter
          l'écran. */}
      <div id="nous-ecrire"
           className="mt-8 rounded-carte shadow-carte bg-[var(--bg-card)] p-6 flex flex-wrap items-center gap-3">
        <p className="text-[14px] text-[var(--text-main)] flex-1 min-w-[16rem]">
          Vous n’avez pas trouvé votre réponse, ou vous avez une idée ?
        </p>
        <button type="button" onClick={() => setRetour('probleme')} className="act act-contour">
          <TriangleAlert className="w-4 h-4" aria-hidden="true" /> Signaler un problème
        </button>
        <button type="button" onClick={() => setRetour('suggestion')} className="act act-contour">
          <MessageSquarePlus className="w-4 h-4" aria-hidden="true" /> Commentaires et suggestions
        </button>
      </div>
      {retour && <FenetreRetour nature={retour} onClose={() => setRetour(null)} />}

      {/* Checklist rapide */}
      <div className="mt-8 bg-[var(--bg-card)] rounded-carte shadow-carte p-6 shadow-sm">
        <h3 className="font-semibold text-[var(--text-bold)] mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-[var(--accent-texte)]" />
          Checklist de démarrage
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            'Créer les comptes coaches (page Coaches)',
            'Préparer les modules de formation (page Formation)',
            'Uploader les outils dans le Kit ASSO PRO',
            'Créer un projet pour l\'association',
            'Affecter un coach au projet',
            'Informer l\'association de se connecter',
            'Suivre la progression régulièrement',
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-pastille bg-[var(--bg-main)]">
              <div className="w-6 h-6 rounded-controle border-2 border-[var(--accent)] shrink-0 mt-0.5 flex items-center justify-center">
                <span className="text-[9px] font-semibold text-[var(--accent-texte)]">{i + 1}</span>
              </div>
              <p className="text-sm text-[var(--text-main)]">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
